// ----------------------------------------------------------------------------
// OwnerSetupWizardView
// ----------------------------------------------------------------------------
// Four-step admin wizard that captures the SaaS owner's runtime configuration
// (AI key/model, Stripe, database, scrapers) and simulates the platform
// activation sequence at the end of step 4.
//
// On "Save & Initialize SaaS Engine":
//   1. Settings are committed to the OwnerSettings context (localStorage).
//   2. A deployment-script modal cycles through 4 system messages over 3s.
//   3. The modal transitions to a "platform activated" success banner.
//
// To wire this to a real backend, replace `runDeploymentSimulation()` below
// with a `POST /api/owner/initialize` call and stream stage progress over
// WebSocket/SSE. The settings persistence already moves through the context's
// `updateSettings` and `commitInitialization` helpers — both are API-ready
// drop-in points.
// ----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  KeyRound,
  Cpu,
  CreditCard,
  Database,
  ShieldCheck,
  Rocket,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
  Globe,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { useOwnerSettings } from "../../context/OwnerSettingsContext";
import { WIZ } from "../../constants/testIds";

// ----------------------------------------------------------------------------
// Static config
// ----------------------------------------------------------------------------
const STEPS = [
  { id: 1, label: "AI Engine Core", icon: Cpu },
  { id: 2, label: "Monetization", icon: CreditCard },
  { id: 3, label: "Data Pipeline", icon: Database },
  { id: 4, label: "Deployment", icon: Rocket },
];

const MODEL_OPTIONS = [
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
  { value: "gemini-pro", label: "Gemini Pro" },
];

const SCRAPER_OPTIONS = [
  "Brevard/Central FL",
  "South FL",
  "West Coast FL",
  "National Feeds",
];

// Build the deployment stage list dynamically — stage 2 echoes the user's
// inputted BaaS endpoint URL so the script feels live.
//
// PRODUCTION LAUNCH NOTE:
//   const supabase = createClient(supabaseUrl, supabaseKey);
//   // RLS policies + tables are then provisioned via supabase.from(...).
const buildDeployStages = ({ supabaseUrl }) => [
  "Mapping serverless environment variable schemas…",
  `Connecting network socket to cloud endpoint: ${
    supabaseUrl || "https://your-project-id.supabase.co"
  }`,
  "Instantiating client authentication SDK using Anon API Key…",
  "Securing client table routes with automated Row-Level Security…",
];

// Mask any secret — keep first 3 + last 4 chars for visual confirmation.
const mask = (s) => {
  if (!s) return "Not provided";
  if (s.length <= 8) return "•".repeat(s.length);
  return `${s.slice(0, 3)}${"•".repeat(Math.max(s.length - 7, 4))}${s.slice(-4)}`;
};

const modelLabel = (v) => MODEL_OPTIONS.find((m) => m.value === v)?.label || v;

// Step tracker style helpers. Extracted from inline nested ternaries for
// readability and to keep the JSX flat.
const trackerCardClass = (isActive, isDone) => {
  if (isActive) return "border-sky-300 bg-sky-50/60 shadow-sm";
  if (isDone) return "border-emerald-200 bg-emerald-50/40";
  return "border-slate-200 bg-white";
};
const trackerIconClass = (isActive, isDone) => {
  if (isActive) return "bg-sky-600 text-white border-sky-600";
  if (isDone) return "bg-emerald-500 text-white border-emerald-500";
  return "bg-slate-50 text-slate-500 border-slate-200";
};

// ============================================================================
// Deployment simulation modal — invoked from step 4.
// Stages are built dynamically from the wizard's BaaS settings so the
// "live" script echoes the owner's chosen project URL.
// ============================================================================
const DeploymentModal = ({ open, stages, onClose, onComplete }) => {
  const [done, setDone] = useState([]);
  const [phase, setPhase] = useState("running"); // 'running' | 'success'
  const timers = useRef([]);
  // Stable refs to avoid restarting the simulation when the parent re-renders
  // (which happens once `commitInitialization` flips `initializedAt`).
  const onCompleteRef = useRef(onComplete);
  const stagesRef = useRef(stages);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    stagesRef.current = stages;
  });

  useEffect(() => {
    if (!open) return;
    setDone([]);
    setPhase("running");
    timers.current.forEach(clearTimeout);

    // N checkmarks evenly across ~3 seconds, then transition to success.
    const snapshot = stagesRef.current;
    const stageSpacing = Math.floor((3000 - 400) / Math.max(snapshot.length, 1));
    timers.current = snapshot.map((_, i) =>
      setTimeout(() => setDone((prev) => [...prev, i]), 400 + i * stageSpacing),
    );
    timers.current.push(
      setTimeout(() => {
        setPhase("success");
        onCompleteRef.current?.();
      }, 3000),
    );

    return () => timers.current.forEach(clearTimeout);
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid={WIZ.deployModal}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
    >
      <div className="absolute inset-0 bg-slate-900/65 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-xl shadow-2xl border border-slate-700 bg-[#0f172a] text-slate-100 overflow-hidden">
        {/* Console header */}
        <div className="px-5 py-3 border-b border-slate-700/70 bg-[#141d2b] flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          <span className="ml-3 text-[11px] font-mono text-slate-400">
            scpp · deploy.sh
          </span>
        </div>

        <div className="px-6 py-6 min-h-[280px] flex flex-col">
          {phase === "running" && (
            <ul className="space-y-2 font-mono text-[12.5px] leading-relaxed flex-1">
              {stages.map((line, i) => {
                const completed = done.includes(i);
                return (
                  <li
                    key={line}
                    className={`flex items-start gap-2 transition-opacity ${
                      completed ? "opacity-100" : "opacity-40"
                    }`}
                  >
                    {completed ? (
                      <CheckCircle2
                        size={14}
                        strokeWidth={2}
                        className="text-emerald-400 mt-0.5 flex-shrink-0"
                      />
                    ) : (
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-500 flex-shrink-0 animate-pulse" />
                    )}
                    <span className={completed ? "text-emerald-300" : "text-slate-400"}>
                      [{completed ? "✓" : " "}] {line}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {phase === "success" && (
            <div
              data-testid={WIZ.deploySuccess}
              className="flex-1 flex flex-col items-center justify-center text-center py-4 animate-fade-in"
            >
              <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-4">
                <CheckCircle2 size={36} strokeWidth={1.8} className="text-emerald-400" />
              </div>
              <h3 className="font-display text-xl font-semibold text-white mb-1.5">
                SaaS Platform Successfully Activated & Live Globally!
              </h3>
              <p className="text-xs text-slate-400 max-w-sm">
                All neural routes, payment rails, and municipal scrapers are
                live. Your vendor cohort can now access the workspace.
              </p>
              <button
                type="button"
                data-testid={WIZ.deployClose}
                onClick={onClose}
                className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold transition-colors"
              >
                Enter Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Step body components — extracted from the parent to reduce its cyclomatic
// complexity. Each component receives only what it needs from the wizard.
// ============================================================================

const Step1AiEngine = ({ settings, updateSettings }) => (
  <div className="space-y-5">
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900">
        AI Engine Core
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Connect the LLM that will power proposal generation across the entire
        vendor cohort.
      </p>
    </div>
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        OpenAI Secret API Key
      </label>
      <div className="relative">
        <KeyRound size={15} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={WIZ.inputOpenAi}
          type="password"
          value={settings.openAiKey}
          onChange={(e) => updateSettings({ openAiKey: e.target.value })}
          placeholder="sk-•••• ••••"
          className="pl-9 h-10 font-mono"
        />
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        Stored locally for the demo. Wire to a server-side secret store before
        production launch.
      </p>
    </div>
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        Target AI Brain
      </label>
      <Select
        value={settings.targetModel}
        onValueChange={(v) => updateSettings({ targetModel: v })}
      >
        <SelectTrigger data-testid={WIZ.selectModel} className="h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MODEL_OPTIONS.map((m) => (
            <SelectItem
              key={m.value}
              value={m.value}
              data-testid={`wiz-model-opt-${m.value}`}
            >
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

const Step2Monetization = ({ settings, updateSettings }) => (
  <div className="space-y-5">
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900">
        Monetization Setup
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Plug in Stripe and choose your default monthly subscription price per
        vendor seat.
      </p>
    </div>
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        Stripe Publishable Key
      </label>
      <div className="relative">
        <CreditCard size={15} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={WIZ.inputStripe}
          value={settings.stripeKey}
          onChange={(e) => updateSettings({ stripeKey: e.target.value })}
          placeholder="pk_live_•••"
          className="pl-9 h-10 font-mono"
        />
      </div>
    </div>
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        Monthly Subscription Fee ($)
      </label>
      <Input
        data-testid={WIZ.inputFee}
        type="number"
        min="0"
        value={settings.subscriptionFee}
        onChange={(e) =>
          updateSettings({ subscriptionFee: Number(e.target.value) || 0 })
        }
        className="h-10 font-mono max-w-[200px]"
      />
    </div>
  </div>
);

const Step3DataPipeline = ({ settings, updateSettings, toggleScraper }) => (
  <div className="space-y-5">
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900">
        Cloud Database & Multi-Tenant Authentication Core
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Bind a Backend-as-a-Service project (Supabase or Firebase) to power
        the vendor login gateway, RLS-scoped data tables, and the regional
        scraping pipes.
      </p>
    </div>

    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        Supabase / Firebase Project URL
      </label>
      <div className="relative">
        <Database size={15} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={WIZ.inputSupabaseUrl}
          value={settings.supabaseUrl}
          onChange={(e) => updateSettings({ supabaseUrl: e.target.value })}
          placeholder="https://your-project-id.supabase.co"
          className="pl-9 h-10 font-mono"
        />
      </div>
    </div>

    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
        Public Anon API Key
      </label>
      <div className="relative">
        <KeyRound size={15} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={WIZ.inputSupabaseKey}
          type="password"
          value={settings.supabaseKey}
          onChange={(e) => updateSettings({ supabaseKey: e.target.value })}
          placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
          className="pl-9 h-10 font-mono"
        />
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        Safe to embed client-side. Backend tables remain protected by the
        row-level-security policies provisioned below.
      </p>
    </div>

    <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3">
      <ShieldCheck size={18} strokeWidth={1.7} className="text-emerald-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          Enable Row-Level Security (RLS) Isolation
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          Auto-scopes every read/write to the signed-in tenant. Strongly
          recommended for multi-vendor production.
        </p>
      </div>
      <Switch
        data-testid={WIZ.rlsToggle}
        checked={settings.rlsEnabled}
        onCheckedChange={(v) => updateSettings({ rlsEnabled: v })}
        className="data-[state=checked]:bg-emerald-600"
      />
    </div>

    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-2.5">
        Activate Regional Scraping Pipes
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {SCRAPER_OPTIONS.map((region) => {
          const active = settings.activeScrapers.includes(region);
          return (
            <label
              key={region}
              className={`flex items-center gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                active
                  ? "border-sky-300 bg-sky-50/70"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Checkbox
                data-testid={WIZ.scraper(region)}
                checked={active}
                onCheckedChange={() => toggleScraper(region)}
              />
              <span className="flex items-center gap-1.5 text-sm text-slate-800">
                <Globe size={12} strokeWidth={1.7} className="text-slate-400" />
                {region}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  </div>
);

// Row config for the Step-4 summary card.
const summaryRows = (settings) => [
  { label: "OpenAI Secret Key", value: mask(settings.openAiKey), mono: true },
  { label: "Target AI Brain", value: modelLabel(settings.targetModel) },
  { label: "Stripe Publishable Key", value: mask(settings.stripeKey), mono: true },
  { label: "Subscription Fee", value: `$${settings.subscriptionFee} / month` },
  { label: "BaaS Project URL", value: settings.supabaseUrl || "Not provided", mono: true },
  { label: "Anon API Key", value: mask(settings.supabaseKey), mono: true },
  { label: "Row-Level Security", value: settings.rlsEnabled ? "Enabled" : "Disabled" },
  {
    label: "Active Scrapers",
    value:
      settings.activeScrapers.length > 0
        ? settings.activeScrapers.join(" · ")
        : "None selected",
  },
];

const Step4Deployment = ({ settings, onInitialize }) => (
  <div className="space-y-5">
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900">
        System Deployment
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Review your configuration. Hitting deploy will lock these values into
        the runtime.
      </p>
    </div>

    <div className="rounded-lg border border-slate-200 bg-slate-50/60 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-2">
        <ShieldCheck size={15} strokeWidth={1.7} className="text-sky-600" />
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-700">
          Configuration Summary
        </p>
      </div>
      <dl className="divide-y divide-slate-200">
        {summaryRows(settings).map((row) => (
          <div
            key={row.label}
            className="px-5 py-3 flex items-center justify-between gap-4"
          >
            <dt className="text-xs text-slate-500 uppercase tracking-[0.08em] font-semibold">
              {row.label}
            </dt>
            <dd
              className={`text-sm text-slate-900 text-right truncate ${
                row.mono ? "font-mono text-[12.5px]" : "font-medium"
              }`}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>

    <button
      type="button"
      data-testid={WIZ.initialize}
      onClick={onInitialize}
      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-[#0c63e7] hover:bg-[#0a4eb8] text-white font-semibold text-sm shadow-sm transition-colors"
    >
      <Sparkles size={16} strokeWidth={2} />
      Save & Initialize SaaS Engine
    </button>
  </div>
);

// ============================================================================
// Wizard root
// ============================================================================
export const OwnerSetupWizardView = () => {
  const { settings, updateSettings, commitInitialization } = useOwnerSettings();
  const [step, setStep] = useState(1);
  const [deployOpen, setDeployOpen] = useState(false);

  // Toggle a scraper region on/off inside the current settings.
  const toggleScraper = (region) => {
    const has = settings.activeScrapers.includes(region);
    updateSettings({
      activeScrapers: has
        ? settings.activeScrapers.filter((r) => r !== region)
        : [...settings.activeScrapers, region],
    });
  };

  // --------------------------------------------------------------------------
  // runDeploymentSimulation — Step 4 primary CTA handler.
  //
  // The visual cycle is purely client-side. To make this a real production
  // bootstrap, replace the body with:
  //
  //   const supabase = createClient(settings.supabaseUrl, settings.supabaseKey);
  //   await supabase.rpc('provision_tenant_schema', { rls: settings.rlsEnabled });
  //   await stripe.products.create({ ...productPayload });
  //
  // That single createClient call automatically rewires the auth gateway and
  // every vendor-scoped query in the rest of the dashboard to the buyer's
  // live cloud database — without touching the UI components.
  // --------------------------------------------------------------------------
  const runDeploymentSimulation = () => {
    setDeployOpen(true);
  };

  // Called by the deployment modal once the success state lands.
  const onDeploymentComplete = () => {
    commitInitialization();
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-semibold">
          Admin · Platform Configuration
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          Owner Setup Wizard
        </h1>
        <p className="text-slate-500 text-sm sm:text-base max-w-2xl">
          Configure the runtime brain of your SaaS — connect an AI engine,
          enable monetization, attach your database, and go live in four steps.
        </p>
        {settings.initializedAt && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
            <CheckCircle2 size={13} strokeWidth={2} />
            Platform initialized · {new Date(settings.initializedAt).toLocaleString()}
          </div>
        )}
      </header>

      {/* Progress tracker */}
      <ol className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STEPS.map((s) => {
          const StepIcon = s.icon;
          const isActive = step === s.id;
          const isDone = step > s.id;
          return (
            <li
              key={s.id}
              data-testid={WIZ.step(s.id)}
              className={`relative rounded-lg border p-4 transition-all ${trackerCardClass(isActive, isDone)}`}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className={`h-8 w-8 rounded-md flex items-center justify-center border ${trackerIconClass(isActive, isDone)}`}
                >
                  {isDone ? (
                    <CheckCircle2 size={16} strokeWidth={2} />
                  ) : (
                    <StepIcon size={15} strokeWidth={1.8} />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500">
                    Step {s.id}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {s.label}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Step content card */}
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-6 sm:px-8 py-7 space-y-6 min-h-[320px]">
          {step === 1 && (
            <Step1AiEngine settings={settings} updateSettings={updateSettings} />
          )}
          {step === 2 && (
            <Step2Monetization settings={settings} updateSettings={updateSettings} />
          )}
          {step === 3 && (
            <Step3DataPipeline
              settings={settings}
              updateSettings={updateSettings}
              toggleScraper={toggleScraper}
            />
          )}
          {step === 4 && (
            <Step4Deployment
              settings={settings}
              onInitialize={runDeploymentSimulation}
            />
          )}
        </div>

        {/* Step nav footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <button
            type="button"
            data-testid={WIZ.back}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14} strokeWidth={1.9} /> Back
          </button>
          <span className="text-xs text-slate-500">Step {step} of 4</span>
          <button
            type="button"
            data-testid={WIZ.next}
            onClick={() => setStep((s) => Math.min(4, s + 1))}
            disabled={step === 4}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight size={14} strokeWidth={1.9} />
          </button>
        </div>
      </section>

      <DeploymentModal
        open={deployOpen}
        stages={buildDeployStages({ supabaseUrl: settings.supabaseUrl })}
        onClose={() => setDeployOpen(false)}
        onComplete={onDeploymentComplete}
      />
    </div>
  );
};
